import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import { Navigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  Feature,
  FeatureBase,
  IStringHash, 
  ModelBase
} from "@models";
import { 
  Creature, 
  TemplateBase,
  UpgradableNumber,
  HitDiceUpgradable,
  AbilityScoresUpgradable,
  Spell,
  Resource,
  TableBase,
  TableEntry,
  CreatureInstance,
  SpellcastingEntry,
  LegendariesTable,
  Legendaries,
  SKILLS,
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  CREATURE_TYPES, 
  SIZE_OPTIONS,
  SLOT_LEVELS,
  DICE_SIZES,
  CR_OPTIONS,
  SIZE_MAP,
  CREATURE_ALIGNMENTS,
} from "@dndModels/Constants";
import { AbilityScoreType, CreatureType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

import TemplateBox from "@dndComponents/TemplateBox";

import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import FloatMenu from "@components/input/menus/FloatMenu";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CreatureAbilityScoresUpgradableInput from "@dndComponents/creature/CreatureAbilityScoresUpgradableInput";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import DeleteButton from "@components/input/buttons/DeleteButton";
import AddButton from "@components/input/buttons/AddButton";
import CheckBox from "@components/input/CheckBox";
import TableInput from "@dndComponents/other/TableInput";
import ButtonBox from "@components/input/buttons/ButtonBox";
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import CreatureSpells from "@dndComponents/creature/CreatureSpells";
import CreatureAttributes from "@dndComponents/creature/CreatureAttributes";
import ArrowBackButton from "@components/input/buttons/ArrowBackButton";
import LegendariesTableInput from "@dndComponents/other/LegendariesTableInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

type TabType = "main" | "hp" | "attributes" | "more_attributes" | "even_more_attributes" | "ability_scores" | "resources" | "actions" | "legendary_actions" | "lair_actions" | "traits" | "spells" | "table" | "parse";

export default function CreatureEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Creature>(api.applyWorkingOn(new Creature()));
  const [spells, setSpells] = useState<Spell[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState<TabType>("main");
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  const [parse_string, setParseString] = useState("");
  const [resource_cr, setResourceCR] = useState(-1);
  const [legendaries_cr, setLegendariesCR] = useState(-1);
  const [creature_instances, setCreatureInstances] = useState<CreatureInstance[]>([]);
  const [level_filter, setLevelFilter] = useState(-1);
  const [topHeight, setTopHeight] = useState(0);
  const [ready_for_height, setReadyForHeight] = useState(false);
    
  const heightFinder: any = React.createRef();

  const parseRef = useRef<HTMLDivElement>(null);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  // const formHeight = window.innerHeight - (window.innerWidth > 697 ? 280 : 280) - (tab === "main" ? 45 : 0);

  const updateCreatureInstances = useCallback(async (use_obj: Creature) => {
    const crs = CR_OPTIONS.map(o => +o).filter(o => o >= use_obj.min_challenge_rating && o <= use_obj.max_challenge_rating);
    
    const new_cis: CreatureInstance[] = [];
    for (const cr of crs) {
      const ci = new CreatureInstance();
      await ci.copyCreature(use_obj, cr);
      new_cis.push(ci);
    }
    
    setCreatureInstances(new_cis);
  }, []);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["tool","condition","spell","resource","creature"]});
    setResources(res.resource as Resource[]);
    setSpells(res.spell as Spell[]);
    
    if (id && id !== "") {
      const creatures = res.creature;
      // debugger;
      const c = creatures.find(o => o._id === id);
      if (c instanceof Creature) {
        await api.loadObject(c);
        setObj(c);
        updateCreatureInstances(c);
        if (c.legendaries_table.entries.length > 0) {
          const min = Math.min(...c.legendaries_table.entries.map(o => o.level));
          setLegendariesCR(min);
        }
        if (c.resources_table.entries.length > 0) {
          const min = Math.min(...c.resources_table.entries.map(o => o.level));
          setResourceCR(min);
        }
        setLoading(false);
        setReadyForHeight(true);
      } else {
        debugger;
      }
    } else {
      // It's here from clicking Create on index.  
      // If the user is not admin, then we need to give the floating feature their id as owner or it will be disabled.
      const new_obj = obj;
      if (api.user && !api.user.admin && new_obj.name === "" && new_obj.owner_id === "") {
        new_obj.owner_id = api.user._id;
      }
      setObj(new_obj);
      updateCreatureInstances(new_obj);
      setChangeTime(common_util.get_time_number());
      setLoading(false);
      setReadyForHeight(true);
    }
  }, [id, obj, updateCreatureInstances]);

  const updateHeight = useCallback(() => {
    // console.log(heightFinder);
    if (heightFinder.current instanceof HTMLElement) {
      setTopHeight(heightFinder.current.clientHeight + 25);
    }
  }, [heightFinder]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  }, [fetched, updateHeight, load]);

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      setObj(obj);
      setSubmitting(true);
    }
  }

  useEffect(() => {
    if (submitting) {
      setSubmitting(false);
      // Check for unsaved ffs.
      const fb_with_unsaved = obj.fbs.find(o => o.features.filter(o2 => o2.floating_feature_id === "").length > 0);
      // console.log(fb_with_unsaved?.name??"None");
      if (fb_with_unsaved && fb_with_unsaved.features[0] && fb_with_unsaved.features[0].floating_feature) {
        const new_ff = fb_with_unsaved.features[0].floating_feature;

        api.upsertObject("floating_feature", new_ff).then(res => {
          if (res) {
            new_ff._id = res.body.id;
            api.add_floating_feature(new_ff);
            fb_with_unsaved.features[0].floating_feature_id = new_ff._id;
            setSubmitting(true);
          }
        });
      } else {
        // debugger;
        api.upsertObject("creature", obj).then(res => {
          setProcessing(false);
          setNavigateTo("/dandd/creature");
        });
      }
    }
  }, [submitting, setSubmitting, obj]);

  useEffect(() => {
    if (ready_for_height) {
      setReadyForHeight(false);
      updateHeight();
    }
     
  }, [ready_for_height, setReadyForHeight, updateHeight]);

  const render_tab = () => {
    switch (tab) {
      case "ability_scores": 
        return (
          <CreatureAbilityScoresUpgradableInput
            obj={obj}
            disabled={disabled}
            onChange={(changed: AbilityScoresUpgradable) => {
              // console.log(changed);
              const new_obj = obj;
              new_obj.ability_scores = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      case "attributes":
        return (
          <CreatureAttributes 
            obj={obj} 
            disabled={disabled} 
            onChange={() => {
              const new_obj = obj;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      case "resources": return renderResources();
      case "actions": return renderActions();
      case "legendary_actions": return renderLegendaryActions();
      case "lair_actions": return renderLairActions();
      case "traits": return renderTraits();
      case "spells":
        return (
          <CreatureSpells
            obj={obj}
            disabled={disabled}
            onChange={() => {
              const new_obj = obj;
              setObj(new_obj);
            }}
          />
        ); 
      case "parse": return renderParse();
      case "hp": return renderHP();
      case "more_attributes": return renderMoreAttributes();
      case "even_more_attributes": return renderEvenMoreAttributes();
      case "table": return renderTable();
      default: return renderMain();
    }
  }

  const renderResources = () => {
    const cr_options = CR_OPTIONS.filter(o => 
      +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating 
    );
    const use_entry = obj.resources_table.getEntry(resource_cr);
    return (
      <Grid container spacing={0} direction="row">
        { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
          (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.resources_table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.resources_table.copyBase(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox 
            name="Select CR for Resources"
            options={cr_options}
            value={`${resource_cr}`}
            onChange={(changed: string) => {
              const new_obj = obj;
              const new_cr = +changed;
              // let resources = new_obj.old_resources[new_cr];
              // if (!resources) {
              //   const highest_lower_cr = Math.max(-1,...Object.keys(new_obj.old_resources).map(o => +o).filter(o => o < new_cr));
              //   resources = [];
              //   if (highest_lower_cr > 0) {
              //     const lower_cr_resources = new_obj.old_resources[highest_lower_cr];
              //     resources = lower_cr_resources.map(o => {
              //       return {...o};
              //     });
              //   } else {
              //     const lowest_higher_cr = Math.min(200,...Object.keys(new_obj.old_resources).map(o => +o).filter(o => o > new_cr));
              //     resources = [];
              //     if (lowest_higher_cr < 200) {
              //       const higher_cr_resources = new_obj.old_resources[lowest_higher_cr];
              //       resources = higher_cr_resources.map(o => {
              //         return {...o};
              //       });
              //     }
              //   }
              //   new_obj.old_resources[new_cr] = resources;
              //   setObj(new_obj);
              //   setChangeTime(common_util.get_time_number());
              // }
              const old_entry = new_obj.resources_table.getEntry(new_cr);
              if (!old_entry || old_entry.level !== new_cr) {
                const entry = old_entry ? old_entry.clone() : new TableEntry();
                entry.level = new_cr;
                new_obj.resources_table.entries.push(entry);
              } else {
                const entry = new TableEntry();
                entry.level = new_cr;
                new_obj.resources_table.entries.push(entry);
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
              setResourceCR(new_cr);
              setReloading(true);
            }}
          />
        </Grid>
        { use_entry && use_entry.level === resource_cr &&
          <>
            <Grid
              size={{
                xs: 10
              }}>
              <SelectObjectBox
                name="New Resource"
                data_type="resource"
                extra_options={SLOT_LEVELS.filter(o => Object.keys(use_entry.data).filter(o2 => o2 === o).length === 0)}
                filter={{ _id: { "$nin": Object.keys(use_entry.data) }}}
                value=""
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const changing_entry = new_obj.resources_table.getEntry(resource_cr);
                  if (changing_entry) {
                    changing_entry.data[changed] = 1;
                    if (!new_obj.resources_table.fields.includes(changed)) {
                      new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = changed;
                    }
                    if (!changed.includes("Slot")) {
                      const resource = resources.find(o => o._id === changed);
                      if (resource && resource.default_dice_size !== 1) {
                        changing_entry.data[`${changed}_size`] = resource.default_dice_size;
                        if (!new_obj.resources_table.fields.includes(`${changed}_size`)) {
                          new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = `${changed}_size`;
                        }
                      }
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <DeleteButton
                label="Delete Resource Level"
                onClick={() => {
                  const new_obj = obj;
                  new_obj.resources_table.entries = new_obj.resources_table.entries.filter(o => o.level !== resource_cr);
                  const other_crs = new_obj.resources_table.entries.map(o => o.level);
                  if (other_crs.length > 0) {
                    setResourceCR(+other_crs[0]);
                  } else {
                    setResourceCR(-1);
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              />
            </Grid>
            { !reloading && 
              obj.resources_table.fields.filter(o => !o.endsWith("_size")).map((field, key) => {
                const resource = field.includes("Slot") ? undefined : resources.find(o => o._id === field);
                const count = use_entry.data[field];
                const size = use_entry.data[`${field}_size`];
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      size={{
                        xs: 4
                      }}>
                      { field.includes("Slot") ? field : 
                        <DisplayObjects type="resource" ids={[field]} />
                      }
                    </Grid>
                    <Grid
                      size={{
                        xs: 2
                      }}>
                      { resource && resource.default_dice_size !== 1 &&
                        <SelectStringBox
                          name="Dice Size"
                          options={DICE_SIZES}
                          value={`${size}`}
                          disabled={disabled}
                          onChange={(changed: string) => {
                            const new_obj = obj;
                            const new_entry = new_obj.resources_table.getEntry(resource_cr);
                            if (new_entry) {
                              new_entry.data[`${field}_size`] = +changed;
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                            }
                          }}
                        />
                      }
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <StringBox 
                        name="Count"
                        value={`${count}`}
                        type="number"
                        disabled={disabled}
                        onBlur={(changed: string) => {
                          const new_obj = obj;
                          const new_entry = new_obj.resources_table.getEntry(resource_cr);
                          if (new_entry) {
                            new_entry.data[field] = +changed;
                            setObj(new_obj);
                            setChangeTime(common_util.get_time_number());
                          }
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <DeleteButton
                        label="Remove Resource"
                        disabled={disabled}
                        onClick={() => {
                          const new_obj = obj;
                          const new_entry = new_obj.resources_table.getEntry(resource_cr);
                          if (new_entry) {
                            delete new_entry.data[field];
                            delete new_entry.data[`${field}_size`];
                            const remove_field = new_obj.resources_table.entries.filter(o => o.data[field] !== undefined).length === 0;
                            if (remove_field) {
                              let indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                              let found = false;
                              for (let i = 0; i < Math.max(...indexes); ++i) {
                                if (found) {
                                  new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                } else {
                                  if (new_obj.resources_table.field_order[i] === field) {
                                    found = true;
                                  }
                                }
                              }
                              delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                              if (new_obj.resources_table.fields.includes(`${field}_size`)) {
                                indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                                found = false;
                                for (let i = 0; i < Math.max(...indexes); ++i) {
                                  if (found) {
                                    new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                  } else {
                                    if (new_obj.resources_table.field_order[i] === `${field}_size`) {
                                      found = true;
                                    }
                                  }
                                }
                                delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                              }
                            }
                            setObj(new_obj);
                            setChangeTime(common_util.get_time_number());
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                );
              })
            }
          </>
        }
      </Grid>
    );
  }

  const renderActions = () => {
    
    if (expanded_feature_base) {
      // Creature FBs are simple, and only ever have one F, and the FB's name and description match the FF.
      const feature = expanded_feature_base.features[0];
      if (feature) {
        const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")}
              feature_source={obj}
              disabled={disabled}
              cfs={creature_instance}
              simple
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Actions";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      }
      return <>Error</>;
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")} 
          feature_source={obj}
          // parent_id={myObj._id} 
          // parent_type="Race"
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Actions";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderLegendaryActions = () => {
    if (expanded_feature_base) {
      const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")}
            feature_source={obj}
            disabled={disabled}
            cfs={creature_instance}
            simple
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Legendary Actions";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const cr_options: string[] = [];
      for (let cr = obj.min_challenge_rating; cr <= obj.max_challenge_rating; ++cr) {
        cr_options.push(`${cr}`);
      }
      const use_entry = obj.legendaries_table.getEntry(legendaries_cr);
      // console.log(use_entry);
      const height = window.innerHeight - 64;
      return (
        <Grid container spacing={0} direction="row">
          { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
            (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
            <Grid
              size={{
                xs: 12
              }}>
              <LegendariesTableInput
                obj={obj.legendaries_table}
                disabled={disabled}
                creature
                onChange={(changed: LegendariesTable) => {
                  const new_obj = obj;
                  new_obj.legendaries_table.copy(changed);
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          }
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox 
              name="Select CR for Legendaries"
              options={cr_options}
              value={`${legendaries_cr}`}
              onChange={(changed: string) => {
                // debugger;
                const new_obj = obj;
                const new_cr = +changed;
                const old_entry = new_obj.legendaries_table.getEntry(new_cr);
                if (!old_entry || old_entry.level !== new_cr) {
                  const entry = old_entry ? old_entry.clone() : new Legendaries();
                  entry.level = new_cr;
                  new_obj.legendaries_table.entries.push(entry);
                } else {
                  const entry = new Legendaries();
                  entry.level = new_cr;
                  new_obj.legendaries_table.entries.push(entry);
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                setLegendariesCR(new_cr);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !reloading &&
              <StringBox
                name="Legendary Actions"
                value={ use_entry ? `${use_entry.actions}` : "0" }
                type="number"
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
                  if (!old_entry) {
                    const entry = new Legendaries();
                    entry.level = legendaries_cr;
                    entry.actions = +changed;
                    new_obj.legendaries_table.entries.push(entry);
                  } else {
                    old_entry.actions = +changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              /> 
            }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !reloading &&
              <StringBox
                name="Legendary Resistances"
                value={ use_entry ? `${use_entry.resistances}` : "0" }
                type="number"
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
                  if (!old_entry) {
                    const entry = new Legendaries();
                    entry.level = legendaries_cr;
                    entry.resistances = +changed;
                    new_obj.legendaries_table.entries.push(entry);
                  } else {
                    old_entry.resistances = +changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <FeatureBasesInput 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")} 
              feature_source={obj}
              // parent_id={myObj._id} 
              // parent_type="Race"
              disabled={disabled}
              onExpand={setExpandedFeatureBase}
              creature_instances={creature_instances}
              level_filter={level_filter}
              onLevelFilterChange={setLevelFilter}
              height={height}
              onAdd={() => {
                const new_obj = obj;
                const new_fb = new FeatureBase();
                new_fb.creature_fb_type = "Legendary Actions";
                new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
                new_fb.feature_source_id = new_obj._id;
                new_fb.feature_source_type = "creature";
                const new_f = new Feature();
                new_f.parent_type = new_fb.feature_source_type;
                new_f.parent_id = new_fb.feature_source_id;
                new_f.base_id = new_fb.id;
                new_fb.features.push(new_f);
                new_obj.fbs.push(new_fb);
                setObj(new_obj);
                setExpandedFeatureBase(new_fb);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      );
    }
  }

  const renderLairActions = () => {
    
    if (expanded_feature_base) {
      const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")}
            feature_source={obj}
            disabled={disabled}
            cfs={creature_instance}
            simple
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Lair Actions";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")} 
          feature_source={obj}
          // parent_id={myObj._id} 
          // parent_type="Race"
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Lair Actions";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderTraits = () => {
    if (expanded_feature_base) {
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")}
            feature_source={obj}
            disabled={disabled}
            simple
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Traits";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")} 
          feature_source={obj}
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Traits";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderParse = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          style={{ maxHeight: "300px", overflowY: "scroll" }}
          size={{
            xs: 6
          }}>
          <StringBox 
            name="Parser"
            value={parse_string}
            multiline
            onBlur={(changed: string) => {
              while (changed.includes("–")) {
                changed = changed.replace("–","-");
              }
              setParseString(changed);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <Button variant="contained" color="primary"
            onClick={parse}>
            Parse
          </Button>
        </Grid>
        {/* <Grid item xs={3}>
          <Button variant="contained" color="primary"
            onClick={reparse}>
            Reparse
          </Button>
        </Grid> */}
        <Grid
          style={{ maxHeight: "300px", overflowY: "scroll" }}
          size={{
            xs: 12
          }}>
          <div ref={parseRef} dangerouslySetInnerHTML={{ __html: parse_string }}></div>
        </Grid>
      </Grid>
    );
  }

  const renderHP = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Hit Points Called"
            value={obj.hit_points_called}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.hit_points_called = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { !disabled &&
            <AddButton
              label="Hit Dice"
              label_separate
              onClick={ () => {
                const new_obj = obj;
                new_obj.hit_dice.push(new HitDiceUpgradable());
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <UpgradableNumberBox 
            name="Hit Dice Bonus"
            value={ obj.hit_dice_bonus }
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["CON"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.hit_dice_bonus = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox 
            name="Set Starting HP"
            value={ obj.starting_hp !== null }
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.starting_hp = changed ? new UpgradableNumber() : null;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { obj.starting_hp && 
            <UpgradableNumberBox 
              name="Starting HP"
              value={ obj.starting_hp }
              disabled={disabled}
              attached_table={obj.table}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.starting_hp = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !reloading && obj.hit_dice.map((hd, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <UpgradableNumberBox
                    name="Hit Dice Count"
                    value={hd.count} 
                    disabled={disabled}
                    attached_table={obj.table}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      const hd_finder = new_obj.hit_dice.filter(o => o.true_id === hd.true_id);
                      if (hd_finder.length === 1) {
                        hd_finder[0].count = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  Hit Dice Size is determined by Creature Size
                  {/* <SelectStringBox 
                    options={["d4","d6","d8","d10","d12","d20"]}
                    value={`d${hd.size}`} 
                    name="Hit Dice Size"
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const hd_finder = new_obj.hit_dice.filter(o => o.true_id === hd.true_id);
                      if (hd_finder.length === 1) {
                        hd_finder[0].size = +(changed.substring(1));
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  /> */}
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { !disabled &&
                    <DeleteButton
                      disabled={disabled}
                      onClick={ () => {
                        const new_obj = obj;
                        new_obj.hit_dice = new_obj.hit_dice.filter(o => o.true_id !== hd.true_id);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <UpgradableNumberBox 
            name="Finishing Blow"
            value={ obj.finishing_blow }
            disabled={disabled}
            attached_table={obj.table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.finishing_blow = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !disabled &&
            <AddButton
              label="Add Extra HP Pool"
              label_separate
              onClick={ () => {
                const new_obj = obj;
                new_obj.extra_hp_pools.push({ true_id: uuidv4().toString(), called: "", max_hp: new UpgradableNumber() });
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !reloading && obj.extra_hp_pools.map((hpp, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <StringBox
                    name="HP Pool Called"
                    value={hpp.called} 
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      const finder = new_obj.extra_hp_pools.find(o => o.true_id === hpp.true_id);
                      if (finder) {
                        finder.called = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <UpgradableNumberBox
                    name="Max HP"
                    value={hpp.max_hp} 
                    disabled={disabled}
                    attached_table={obj.table}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      const finder = new_obj.extra_hp_pools.find(o => o.true_id === hpp.true_id);
                      if (finder) {
                        finder.max_hp = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { !disabled &&
                    <DeleteButton
                      disabled={disabled}
                      onClick={ () => {
                        const new_obj = obj;
                        new_obj.extra_hp_pools = new_obj.extra_hp_pools.filter(o => o.true_id !== hpp.true_id);
                        setObj(new_obj);
                        updateCreatureInstances(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    ); 
  }

  const renderMoreAttributes = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox 
            name="Spellcasting Ability"
            value={obj.spellcasting_ability} 
            options={ABILITY_SCORES}
            extra_options={["None"]}
            disabled={disabled}
            onChange={(changed: AbilityScoreType | "None") => {
              const new_obj = obj;
              new_obj.spellcasting_ability = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox
            name="Spell DC"
            value={ obj.dc } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DC INT","DC WIS","DC CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.dc = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Spell Attack"
            value={ obj.magic_attack } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["Attack INT","Attack WIS","Attack CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.magic_attack = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Non-Magic Attack"
            value={ obj.non_magic_attack } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["Attack STR","Attack DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.non_magic_attack = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Magic Damage Bonus"
            value={ obj.magic_dmg_bonus }
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["INT","WIS","CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.magic_dmg_bonus = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Non-Magic Damage Bonus"
            value={ obj.non_magic_dmg_bonus } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["STR","DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.non_magic_dmg_bonus = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      </Grid>
    ); 
  }

  const renderEvenMoreAttributes = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          style={{ color: "blue", fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          Strings
          <AddButton
            disabled={disabled}
            onClick={ () => {
              const new_obj = obj;
              new_obj.string_hash["New String"] = "";
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { Object.keys(obj.string_hash).map((hash_key, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name="Name"
                  value={ hash_key } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    if (changed !== "" && changed !== hash_key) {
                      const new_obj = obj;
                      new_obj.string_hash[changed] = obj.string_hash[hash_key];
                      delete new_obj.string_hash[hash_key];
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name={ hash_key }
                  value={ obj.string_hash[hash_key] } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    const new_obj = obj;
                    new_obj.string_hash[hash_key] = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DeleteButton
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    delete new_obj.string_hash[hash_key];
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
        <Grid
          style={{ color: "blue", fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          Upgradable Numbers
          <AddButton
            disabled={disabled}
            onClick={ () => {
              const new_obj = obj;
              new_obj.upgradable_hash["New Upgradable"] = new UpgradableNumber();
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { Object.keys(obj.upgradable_hash).map((hash_key, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name="Name"
                  value={ hash_key } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    if (changed !== "" && changed !== hash_key) {
                      const new_obj = obj;
                      new_obj.upgradable_hash[changed] = obj.upgradable_hash[hash_key];
                      delete new_obj.upgradable_hash[hash_key];
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox 
                  name={ hash_key }
                  value={ obj.upgradable_hash[hash_key] } 
                  disabled={disabled}
                  attached_table={obj.table}
                  // no_formula_key
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.upgradable_hash[hash_key] = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DeleteButton
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    delete new_obj.upgradable_hash[hash_key];
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    ); 
  }

  const renderTable = () => {
    
    return (
      <Grid container spacing={0} direction="row">
        { !disabled &&
          <>
            { obj.table.entries.length === 0 ? 
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name="Convert to Table Usage"
                  onClick={() => {
                    const new_obj = obj;
                    const entry = new TableEntry();
                    entry.level = obj.challenge_rating;
                    entry.data = {
                      "STR": obj.ability_scores.strength.base,
                      "DEX": obj.ability_scores.dexterity.base,
                      "CON": obj.ability_scores.constitution.base,
                      "INT": obj.ability_scores.intelligence.base,
                      "WIS": obj.ability_scores.wisdom.base,
                      "CHA": obj.ability_scores.charisma.base,
                    };
                    new_obj.ability_scores.strength.base = 0;
                    new_obj.ability_scores.strength.makeBasic();
                    new_obj.ability_scores.strength.table_type = "Attached";
                    new_obj.ability_scores.strength.table_id = "Attached";
                    new_obj.ability_scores.strength.table_field = "STR";
                    new_obj.ability_scores.dexterity.base = 0;
                    new_obj.ability_scores.dexterity.makeBasic();
                    new_obj.ability_scores.dexterity.table_type = "Attached";
                    new_obj.ability_scores.dexterity.table_id = "Attached";
                    new_obj.ability_scores.dexterity.table_field = "DEX";
                    new_obj.ability_scores.constitution.base = 0;
                    new_obj.ability_scores.constitution.makeBasic();
                    new_obj.ability_scores.constitution.table_type = "Attached";
                    new_obj.ability_scores.constitution.table_id = "Attached";
                    new_obj.ability_scores.constitution.table_field = "CON";
                    new_obj.ability_scores.intelligence.base = 0;
                    new_obj.ability_scores.intelligence.makeBasic();
                    new_obj.ability_scores.intelligence.table_type = "Attached";
                    new_obj.ability_scores.intelligence.table_id = "Attached";
                    new_obj.ability_scores.intelligence.table_field = "INT";
                    new_obj.ability_scores.wisdom.base = 0;
                    new_obj.ability_scores.wisdom.makeBasic();
                    new_obj.ability_scores.wisdom.table_type = "Attached";
                    new_obj.ability_scores.wisdom.table_id = "Attached";
                    new_obj.ability_scores.wisdom.table_field = "WIS";
                    new_obj.ability_scores.charisma.base = 0;
                    new_obj.ability_scores.charisma.makeBasic();
                    new_obj.ability_scores.charisma.table_type = "Attached";
                    new_obj.ability_scores.charisma.table_id = "Attached";
                    new_obj.ability_scores.charisma.table_field = "CHA";
                    if (obj.hit_dice.length === 1 && obj.hit_dice[0].count.isBasic()) {
                      entry.data["Hit Dice Count"] = Math.floor(obj.hit_dice[0].count.base);
                      new_obj.hit_dice[0].count.base = 0;
                      new_obj.hit_dice[0].count.table_type = "Attached";
                      new_obj.hit_dice[0].count.table_id = "Attached";
                      new_obj.hit_dice[0].count.table_field = "Hit Dice Count";
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      entry.data["Armor Class"] = obj.armor_class.base;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    entry.data["Size"] = obj.size_ugn.base;
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    new_obj.table.entries.push(entry);
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Hit Dice Count") ?
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name="Add Missing Fields"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      let count = 0;
                      if (new_obj.hit_dice.length > 0) {
                        count = new_obj.hit_dice[0].count.base;
                        if (!new_obj.hit_dice[0].count.isBasic()) {
                          // if it's not basic it's usually just a CR multiplier,
                          // and we don't want to worry about anything else.
                          count += new_obj.hit_dice[0].count.add_char_level_mult * entry.level;
                        }
                      }
                      entry.data["Hit Dice Count"] = Math.floor(count);
                      if (new_obj.armor_class.add_char_level_mult > 0) {
                        entry.data["Armor Class"] = new_obj.armor_class.base + new_obj.armor_class.add_char_level_mult * entry.level;
                      } else {
                        entry.data["Armor Class"] = new_obj.armor_class.base;
                      }
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    if (obj.hit_dice.length === 1) {
                      new_obj.hit_dice[0].count.makeBasic();
                      new_obj.hit_dice[0].count.base = 0;
                      new_obj.hit_dice[0].count.table_type = "Attached";
                      new_obj.hit_dice[0].count.table_id = "Attached";
                      new_obj.hit_dice[0].count.table_field = "Hit Dice Count";
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      new_obj.armor_class.add_char_level_mult = 0;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";

                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Armor Class") ?
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name="Add Missing Fields"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      if (new_obj.armor_class.add_char_level_mult > 0) {
                        entry.data["Armor Class"] = new_obj.armor_class.base + new_obj.armor_class.add_char_level_mult * entry.level;
                      } else {
                        entry.data["Armor Class"] = new_obj.armor_class.base;
                      }
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      new_obj.armor_class.add_char_level_mult = 0;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Size") &&
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name="Add Missing Field"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
          </>
        }
        { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
          (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.table.copyBase(changed);
                setObj(new_obj);
                updateCreatureInstances(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
      </Grid>
    ); 
  }

  const renderMain = () => {
    // console.log(obj.alignment);
    // console.log(CREATURE_ALIGNMENTS);
    return (
      <Grid container spacing={0} direction="row">
        {/* Names and Alignment */}
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Name"
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Name Invalid"} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.name = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Small Name"
            value={obj.small_name} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.small_name = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Title"
            value={obj.title} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.title = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox 
            name="Alignment"
            options={CREATURE_ALIGNMENTS}
            value={obj.alignment} 
            disabled={disabled}
            // log_it
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.alignment = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        {/* Types */}
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox 
            name="Creature Type" 
            options={ CREATURE_TYPES }
            value={obj.creature_type} 
            disabled={disabled}
            onChange={(changed: CreatureType) => {
              const new_obj = obj;
              new_obj.creature_type = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SuggestionStringBox 
            name="Subtypes"
            field="subtypes"
            values={obj.subtypes}
            disabled={disabled}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.subtypes = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectObjectBox 
            name="Group"
            data_type="creature_group"
            value={obj.group_id}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.group_id = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectObjectBox 
            name="Classes"
            data_type="game_class"
            values={obj.class_ids}
            disabled={disabled}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.class_ids = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        {/* Age and Size */}
        <Grid
          size={{
            xs: 3
          }}>
          <SuggestionStringBox 
            name="Age Category"
            field="age_category"
            value={obj.age_category}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.age_category = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 9
          }}> 
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: obj.size_ugn.isBasic() ? 12 : 6
              }}>
              <SelectStringBox 
                name="Size"
                options={SIZE_OPTIONS}
                extra_options={["Use Upgradable"]}
                value={!obj.size_ugn.isBasic() ? "Use Upgradable" : `${obj.size_ugn.base}`} 
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  if (changed === "Use Upgradable") {
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                  } else {
                    new_obj.size_ugn.makeBasic();
                    new_obj.size_ugn.base = +changed;
                  }
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { !obj.size_ugn.isBasic() &&
              <Grid
                size={{
                  xs: 6
                }}>
                <UpgradableNumberBox
                  name="Size Upgradable"
                  value={ obj.size_ugn } 
                  disabled={disabled}
                  attached_table={obj.table}
                  // no_formula_key
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.size_ugn = changed;
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
          </Grid>
        </Grid>
        {/* Init and AC */}
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Initiative Modifier"
            value={obj.initiative_modifier} 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.initiative_modifier = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Armor Class"
            value={obj.armor_class}
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.armor_class = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="AC Flavor"
            value={obj.ac_flavor} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.ac_flavor = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { renderChallengeRatings() }
        {/* ModelBaseDetails */}
        <Grid
          size={{
            xs: 12
          }}>
          <ModelBaseDetails
            obj={obj}
            no_grid
            disabled={disabled}
            change_time={change_time}
            onChange={(changed: ModelBase) => {
              if (changed instanceof Creature) {
                const new_obj = obj;
                new_obj.copy(changed);
                setObj(new_obj);
                updateCreatureInstances(new_obj);
                setChangeTime(common_util.get_time_number());
              }
            }}
            get_creature_homebrewery_string={get_creature_homebrewery_string}
            onSubmit={submit}
            processing={processing}
          />
        </Grid>
      </Grid>
    ); 
  }

  const renderChallengeRatings = () => {
    // console.log("Rendering");
    return (
      <>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Default Challenge Rating"
            value={`${obj.challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (new_obj.challenge_rating === new_obj.min_challenge_rating && 
                new_obj.min_challenge_rating === new_obj.max_challenge_rating) {
                new_obj.min_challenge_rating = val;
                new_obj.max_challenge_rating = val;
              } else {
                if (val < new_obj.min_challenge_rating) {
                  new_obj.min_challenge_rating = val;
                } else if (val > new_obj.max_challenge_rating) {
                  new_obj.max_challenge_rating = val;
                }
              }
              new_obj.challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              // setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Min Challenge Rating"
            value={`${obj.min_challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (val >= new_obj.max_challenge_rating) {
                new_obj.challenge_rating = val;
                new_obj.max_challenge_rating = val;
                new_obj.name_table = [];
              } else if (val > new_obj.challenge_rating) {
                new_obj.challenge_rating = val;
              }
              new_obj.min_challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Max Challenge Rating"
            value={`${obj.max_challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (val <= new_obj.min_challenge_rating) {
                new_obj.challenge_rating = val;
                new_obj.min_challenge_rating = val;
                new_obj.name_table = [];
              } else if (val < new_obj.challenge_rating) {
                new_obj.challenge_rating = val;
              }
              new_obj.max_challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { obj.min_challenge_rating !== obj.max_challenge_rating && 
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <AddButton
                disabled={disabled || (obj.name_table.length > 0 && Math.max(...obj.name_table.map(o => o.level)) === obj.max_challenge_rating)}
                onClick={() => {
                  const new_obj = obj;
                  if (new_obj.name_table.length === 0) {
                    new_obj.name_table.push({ level: new_obj.min_challenge_rating, name: new_obj.name, age_category: new_obj.age_category });
                  } else {
                    const max = Math.max(...new_obj.name_table.map(o => o.level));
                    const entry = new_obj.name_table.filter(o => o.level === max)[0]; // I already know it exists
                    let level = entry.level;
                    switch (level) {
                      case 0: level = 1/8; break;
                      case 0.125: level = 1/4; break;
                      case 0.25: level = 1/2; break;
                      case 0.5: level = 1; break;
                      default: level++; break;
                    }
                    new_obj.name_table.push({ level, name: entry.name, age_category: entry.age_category });
                  }
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
              &nbsp;
              Names by CR
            </Grid>
            { !reloading && obj.name_table.sort((a,b) => { return a.level - b.level; }).map((name_entry, key) => {
              const other_named_crs = obj.name_table.filter(o => o.level !== name_entry.level).map(o => o.level);
              const cr_options = CR_OPTIONS.filter(o => 
                +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating && 
                !other_named_crs.includes(+o)
              );
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <SelectStringBox
                      name="Starting at CR"
                      value={`${name_entry.level}`}
                      options={cr_options}
                      disabled={disabled}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.level = +changed;
                        }
                        setObj(new_obj);
                        updateCreatureInstances(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <StringBox
                      name="Name"
                      value={name_entry.name}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.name = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <StringBox
                      name="Age Category"
                      value={name_entry.age_category}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.age_category = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DeleteButton
                      disabled={disabled}
                      onClick={() => {
                        const new_obj = obj;
                        new_obj.name_table = new_obj.name_table.filter(o => o.level !== name_entry.level);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </>
        }
      </>
    );
  }

  const get_creature_homebrewery_string = (ci: CreatureInstance | undefined = undefined) => {
    const creature = obj;
    let str = `# ${ci ? ci.name : creature.true_name}\n\r___\n\r`;
    let ac = "See Properties Table";
    let hp = "See Properties Table";
    let ability_scores_string = "";
    let saving_throws = "";
    let skills = "";
    let senses_string = "";
    let tools = "";
    let immunities_string = "";
    let resistances_string = "";
    let vulnerabilities_string = "";
    let condition_immunities_string = "";
    let languages = "";
    let cr_string = "";
    let prof_bonus = "See Properties Table";
    let table_string = "";
    let resource_table_string = "";
    const legendaries_table_string = "";
    let attributes_table_string = "";
    let size = ci ? `${ci.size} ` : creature.size_ugn.isBasic() ? `${SIZE_MAP[creature.size_ugn.base]} ` : "";
    let one_resources_entry: TableEntry | null = null;
    if (ci) {
      ac = `${ci.armor_class}`;
      hp = `${creature.hit_dice_string_for_cr(ci.challenge_rating)}`;
      saving_throws = ci.saving_throws_string;
      skills = ci.skills_string;
      senses_string = ci.senses_string;
      tools = ci.tools_string;
      immunities_string = ci.immunities_string;
      resistances_string = ci.resistances_string;
      vulnerabilities_string = ci.vulnerabilities_string;
      condition_immunities_string = ci.condition_immunities_string;
      languages = ci.languages;
      cr_string = `<b>Challenge</b> ${ci.challenge_rating} (${common_util.cr_to_xp_map[ci.challenge_rating]} XP)\n\r___\n\r`;
      prof_bonus = `+${ci.proficiency_modifier}`;
      ability_scores_string = `<table><tr><th>STR</th><th>DEX</th><th>CON</th></tr>
        <tr>
          <td>${ci.current_ability_scores.strength} (${common_util.addPlusMaybe(ci.current_ability_scores.STR)})</td>
          <td>${ci.current_ability_scores.dexterity} (${common_util.addPlusMaybe(ci.current_ability_scores.DEX)})</td>
          <td>${ci.current_ability_scores.constitution} (${common_util.addPlusMaybe(ci.current_ability_scores.CON)})</td>
        </tr>
        <tr><th>INT</th><th>WIS</th><th>CHA</th></tr>
        <tr>
          <td>${ci.current_ability_scores.intelligence} (${common_util.addPlusMaybe(ci.current_ability_scores.INT)})</td>
          <td>${ci.current_ability_scores.wisdom} (${common_util.addPlusMaybe(ci.current_ability_scores.WIS)})</td>
          <td>${ci.current_ability_scores.charisma} (${common_util.addPlusMaybe(ci.current_ability_scores.CHA)})</td>
        </tr>
        </table>`;
    } else {
      const attributes = creature.attributes_table.entries[0];
      if (creature.attributes_table.entries.length === 1) {
        saving_throws = attributes.saving_throws_string;
        skills = attributes.skills_string;
        senses_string = attributes.senses_string;
        tools = attributes.tools_string;
        immunities_string = attributes.immunities_string;
        resistances_string = attributes.resistances_string;
        vulnerabilities_string = attributes.vulnerabilities_string;
        condition_immunities_string = attributes.condition_immunities_string;
        languages = attributes.languages;
      } else if (attributes) {
        const unchanged: string[] = [];
        const attributes_table = creature.attributes_table;
        if (attributes_table.save_profs_unchanged) {
          unchanged.push("save_profs");
          saving_throws = attributes.saving_throws_string;
        } else {
          saving_throws = "See Attributes Table";
        }
        // console.log(attributes_table.skill_profs_unchanged);
        if (attributes_table.skill_profs_unchanged) {
          unchanged.push("skill_profs");
          skills = attributes.skills_string;
        } else {
          skills = "See Attributes Table";
        }
        if (attributes_table.tool_profs_unchanged) {
          unchanged.push("tool_profs");
          tools = attributes.tools_string;
        } else {
          tools = "See Attributes Table";
        }
        if (attributes_table.damage_immunities_unchanged) {
          unchanged.push("damage_immunities");
          immunities_string = attributes.immunities_string;
        } else {
          immunities_string = "See Attributes Table";
        }
        if (attributes_table.damage_resistances_unchanged) {
          unchanged.push("damage_resistances");
          resistances_string = attributes.resistances_string;
        } else {
          resistances_string = "See Attributes Table";
        }
        if (attributes_table.damage_vulnerabilities_unchanged) {
          unchanged.push("damage_vulnerabilities");
          vulnerabilities_string = attributes.vulnerabilities_string;
        } else {
          vulnerabilities_string = "See Attributes Table";
        }
        if (attributes_table.condition_immunities_unchanged) {
          unchanged.push("condition_immunities");
          condition_immunities_string = attributes.condition_immunities_string;
        } else {
          condition_immunities_string = "See Attributes Table";
        }
        if (attributes_table.languages_unchanged) {
          unchanged.push("languages");
          languages = attributes.languages;
        } else {
          languages = "See Attributes Table";
        }
        if (attributes_table.senses_unchanged) {
          unchanged.push("senses");
          senses_string = attributes.senses_string;
        } else {
          senses_string = "See Attributes Table";
        }
        
        const attribute_fields = attributes_table.all_attributes.filter(o => !unchanged.includes(o));
        if (attribute_fields.length > 0) {
          // Show the table.
          const attribute_map: { [key: string]: string } = {
            "damage_immunities": "Immunities",
            "damage_resistances": "Resistances",
            "damage_vulnerabilities": "Vulnerabilities",
            "condition_immunities": "Condition Immunities",
            "senses": "Senses",
            "skill_profs": "Skill Proficiencies",
            "save_profs": "Save Proficiencies",
            "speeds": "Speeds",
            "tool_profs": "Tool Proficiencies",
            "languages": "Languages"
          };
          attributes_table_string += "<b>Attributes by CR</b>\n\r";
          const add_attributes_table = (using_fields: string[]) => {
            attributes_table_string += "<table>\n\r";
            attributes_table_string += `<tr><th>CR</th>${using_fields.map(o => { 
              return "<th>" + attribute_map[o] + "</th>"; 
            }).join("")}</tr>\n\r`;
            for (const entry of attributes_table.entries.sort((a, b) => { return a.level - b.level; })) {
              attributes_table_string += "<tr>";
              attributes_table_string += "<td>" + entry.level + "</td>";
              attributes_table_string += using_fields.map(field => { 
                const data = (entry as any)[field];
                let str = "";
                if (typeof data === "object") {
                  if (data.length) {
                    str = data.map((o: any) => `${o}`).join(", ");
                  } else {
                    const keys = Object.keys(data);
                    if (field === "skill_profs") {
                      str = keys.map(key => {
                        const skill = SKILLS.find(o => o._id === key);
                        const prof = +data[key];
                        if (skill) {
                          return `${skill.name}${prof > 1 ? " (Expert)" : ""}`;
                        }
                        return "";
                      }).filter(o => o !== "").join("\n\r___\n\r");
                    } else {
                      str = keys.map(key => {
                        return `${key}: ${data[key]}`;
                      }).join("\n\r___\n\r");
                    }
                  }
                } else {
                  str = `${data}`;
                }
                return "<td>" + str + "</td>"; 
              }).join("");
              attributes_table_string += "</tr>\n\r";
            }
            attributes_table_string += "</table>\n\r___\n\r";
          }
          switch (attribute_fields.length) {
            case 6: 
              if (true) {
                const fields1 = attribute_fields.slice(0, 2);
                const fields2 = attribute_fields.slice(2);
                add_attributes_table(fields1); 
                attributes_table_string += "<b>More Attributes by CR</b>\n\r";
                add_attributes_table(fields2); 
              }
            break;
            case 7: case 8:
              if (true) {
                const fields1 = attribute_fields.slice(0, 3);
                const fields2 = attribute_fields.slice(3);
                add_attributes_table(fields1); 
                attributes_table_string += "<b>More Attributes by CR</b>\n\r";
                add_attributes_table(fields2); 
              }
            break;
            case 9: case 10:
              if (true) {
                const fields1 = attribute_fields.slice(0, 2);
                const fields2 = attribute_fields.slice(2, 5);
                const fields3 = attribute_fields.slice(5);
                add_attributes_table(fields1); 
                attributes_table_string += "<b>More Attributes by CR</b>\n\r";
                add_attributes_table(fields2); 
                attributes_table_string += "<b>Even More Attributes by CR</b>\n\r";
                add_attributes_table(fields3); 
              }
            break;
            default: add_attributes_table(attribute_fields); break;
          }
        }
      }
      // Show the table.
      let table = creature.table;

      if (table.entries.length > 0 && table.fields.length > 0) {
        let show_size = false;
        if (table.fields.includes("Size")) {
          const sizes = table.entries.map(o => o.data["Size"]);
          const min_size = Math.min(...sizes);
          const max_size = Math.max(...sizes);
          if (min_size === max_size) {
            size = SIZE_MAP[min_size];
          } else {
            show_size = true;
          }
        }
        const fields = table.fields.filter(o => o !== "Armor Class" && o !== "Size");
        table_string = "<b>Ability Scores by CR</b>\n\r___\n\r";
        table_string += "<table>\n\r";
        table_string += `<tr><th>CR</th>${fields.map(o => { 
          if (o.length < 4) {
            return "<th>&nbsp;" + o + "&nbsp;</th>"; 
          } else {
            return "<th>" + o + "</th>"; 
          }
        }).join("")}<th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
        for (const entry of table.entries.filter(o => 
          o.level >= creature.min_challenge_rating && 
          o.level <= creature.max_challenge_rating).sort((a, b) => {
          return a.level - b.level;
        })) {
          // console.log(entry);
          table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
        }
        table_string += "</table>\n\r___\n\r";
        table_string = "<b>Creature Properties by CR</b>\n\r___\n\r";
        table_string += "<table>\n\r";
        table_string += `<tr><th>CR</th><th>Proficiency Bonus</th><th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
        let ac_of_others = 0;
        for (const entry of table.entries.filter(o => 
          o.level >= creature.min_challenge_rating && 
          o.level <= creature.max_challenge_rating).sort((a, b) => {
          return a.level - b.level;
        })) {
          // console.log(entry);
          table_string += `<tr><td>${entry.level}</td>`;
          const ac_for_cr = creature.ac_for_cr(entry.level);
          if (ac_of_others === 0) {
            ac_of_others = ac_for_cr;
          } else if (ac_of_others > 0 && ac_of_others !== ac_for_cr) {
            ac_of_others = -1;
          }
          table_string += `<td>${creature.get_proficiency_modifier(entry.level)}</td><td>${creature.max_hp_for_cr(entry.level)}</td><td>${ac_for_cr}</td>${show_size ? "<td>" + SIZE_MAP[entry.data["Size"]] + "</td>" : ""}</tr>\n\r`;
        }
        if (ac_of_others > 0) {
          ac = `${ac_of_others}`;
        }
        table_string += "</table>\n\r___\n\r";
      } else {
        table_string += "TABLE NOT FOUND!\n\r___\n\r";
      }

      table = creature.resources_table;

      if (table.entries.length > 1 && table.fields.length > 0) {
        const fields = table.fields.filter(o => !o.endsWith("_size"));
        resource_table_string = "<b>Resources by CR</b>\n\r___\n\r";
        resource_table_string += "<table>\n\r";
        resource_table_string += `<tr><th>CR</th>${fields.map(o => { 
          if (o.length < 4) {
            return "<th>&nbsp;" + o + "&nbsp;</th>"; 
          } else {
            return "<th>" + o + "</th>"; 
          }
        }).join("")}</tr>\n\r`;
        for (const entry of table.entries.filter(o => 
          o.level >= creature.min_challenge_rating && 
          o.level <= creature.max_challenge_rating).sort((a, b) => {
          return a.level - b.level;
        })) {
          resource_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
        }
        resource_table_string += "</table>\n\r___\n\r";
      } else if (table.entries.length === 1) {
        one_resources_entry = table.entries[0];
      }

      // if (creature.legendaries_table.entries.length > 1) {
      //   legendaries_table_string = "<b>Legendaries by CR</b>\n\r___\n\r";
      //   legendaries_table_string += "<table>\n\r";
      //   legendaries_table_string += `<tr><th>CR</th>${fields.map(o => { 
      //     if (o.length < 4) {
      //       return "<th>&nbsp;" + o + "&nbsp;</th>"; 
      //     } else {
      //       return "<th>" + o + "</th>"; 
      //     }
      //   }).join("")}</tr>\n\r`;
      //   for (const entry of table.entries.filter(o => 
      //     o.level >= creature.min_challenge_rating && 
      //     o.level <= creature.max_challenge_rating).sort((a, b) => {
      //     return a.level - b.level;
      //   })) {
      //     legendaries_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
      //   }
      //   legendaries_table_string += "</table>\n\r___\n\r";
      // } else if (table.entries.length === 1) {
      //   one_resources_entry = table.entries[0];
      // }
    }
    str += `<style>
      div.hr {
        width: 0; 
        height: 0; 
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-left: 300px solid brown;
        }
      td {
        justify-content: center;
        text-align: center;
        align-items: center;
      }
      th {
        font-weight: bold
      }
      b {
        font-weight: bold
      }
      </style>`;
    str += `${size}${creature.creature_type}${creature.alignment === "" ? "" : ", " + creature.alignment}\n\r___\n\r`;
    str += `<div class="hr"></div>\n\r___\n\r`;
    str += `<b>Armor Class</b> ${ac} ${creature.ac_flavor.length > 0 ? "(" + creature.ac_flavor + ")" : ""}\n\r___\n\r`;
    str += `<b>Hit Points</b> ${hp}\n\r___\n\r`;
    str += `<b>Speed</b> ${creature.speed_string}\n\r___\n\r`;
    str += `<div class="hr"></div>\n\r___\n\r`;
    if (ability_scores_string.length > 0) {
      str += ability_scores_string;
      str += `<div class="hr"></div>\n\r___\n\r`;
    }
    if (saving_throws.length > 0) {
      str += `<b>Saving Throws</b> ${saving_throws}\n\r___\n\r`;
    }
    if (skills.length > 0) {
      str += `<b>Skills</b> ${skills}\n\r___\n\r`;
    }
    if (tools.length > 0) {
      str += `<b>Tools</b> ${tools}\n\r___\n\r`;
    }
    if (immunities_string.length > 0) {
      str += `<b>Damage Immunities</b> ${immunities_string}\n\r___\n\r`;
    }
    if (resistances_string.length > 0) {
      str += `<b>Damage Resistances</b> ${resistances_string}\n\r___\n\r`;
    }
    if (vulnerabilities_string.length > 0) {
      str += `<b>Damage Vulnerabilities</b> ${vulnerabilities_string}\n\r___\n\r`;
    }
    if (condition_immunities_string.length > 0) {
      str += `<b>Condition Immunities</b> ${condition_immunities_string}\n\r___\n\r`;
    }
    if (senses_string.length > 0) {
      str += `<b>Senses</b> ${senses_string}\n\r___\n\r`;
    }
    if (languages.length > 0) {
      str += `<b>Languages</b> ${languages}\n\r___\n\r`;
    }
    str += cr_string;
    str += `<b>Proficiency Bonus</b> ${prof_bonus}\n\r___\n\r`;
    str += `<div class="hr"></div>\n\r___\n\r`;
    str += get_homebrewery_fbs_string(ci, one_resources_entry);
    if (creature.description.length > 0) {
      str += "## Description\n\r___\n\r";
      str += `${creature.description}\n\r___\n\r`;
    }
    if (table_string.length > 0 || resource_table_string.length > 0 || attributes_table_string.length > 0 || legendaries_table_string.length > 0) {
      str += "\\page\n\r";
      str += "## Tables\n\r___\n\r";
      if (table_string.length > 0 || resource_table_string.length > 0) {
        str += table_string;
        str += resource_table_string;
        if (attributes_table_string.length > 0) {
          str += "\\column\n\r";
          str += attributes_table_string;
        }
      } else if (attributes_table_string.length > 0) {
        str += attributes_table_string;
      }
      str += legendaries_table_string;
    }
    return str;
  }

  const get_homebrewery_fbs_string = (ci: CreatureInstance | undefined, one_resources_entry: TableEntry | null) => {
    const creature = obj;
    let str = "";
    let fbs = ci ? creature.fbs.filter(o => o.level <= ci.challenge_rating) : creature.traits;
    if (fbs.length > 0 || (creature.legendaries_table.entries.length > 0 && (!ci || creature.legendary_resistances_for_cr(ci.challenge_rating) > 0))) {
      str += "## Traits\n\r___\n\r";
      if (ci) {
        for (const fb of fbs) {
          str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
        }
      } else {
        const crs = (common_util.unique_array(fbs.map(o => +o.level))).sort((a, b) => { return a - b; });
        if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
          for (const fb of fbs) {
            str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
          }
        } else if (crs.length > 0) {
          for (const cr of crs) {
            str += `### Beginning At CR ${cr}\n\r___\n\r`;
            for (const fb of fbs.filter(o => o.level === cr)) {
              str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
            }
          }
        }
      }
      // if (creature.legendaries_table.entries.length > 0) {
      //   if (ci) {
      //     const legendary_resistances = creature.legendary_resistances_for_cr(ci.challenge_rating);
      //     if (legendary_resistances > 0) {
      //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
      //     }
      //   } else {
      //     let legendary_resistances = ""; 
      //     if (creature.legendaries_table.entries.length === 1 && 
      //       creature.legendaries_table.entries[0].level <= creature.min_challenge_rating) {
      //       legendary_resistances = `${creature.legendaries_table.entries[0].data["Legendary Resistances"]}`;
      //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
      //     } else if (creature.legendaries_table.entries.length === 1) {
      //       str += `### Beginning At CR ${creature.legendaries_table.entries[0].level}\n\r___\n\r`;
      //       legendary_resistances = `${creature.legendaries_table.entries[0].data["Legendary Resistances"]}`;
      //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
      //     } else {
      //       str += `<b>Legendary Resistance.</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it may be able to choose to succeed instead.  See the Legendaries Table to see how many uses per day if any it has of this feature.\n\r___\n\r`;
      //     }
      //   }
      // }
    }
    fbs = ci ? creature.action_fbs.filter(o => o.level <= ci.challenge_rating) : creature.action_fbs;
    if (fbs.length > 0 || creature.spellcasting_table.entries.length > 0) {
      str += "## Actions\n\r___\n\r";
      if (ci) {
        const spellcasting_entry = creature.spellcasting_table.getEntry(ci.challenge_rating);
        if (ci.spellcasting_description.length > 0 && spellcasting_entry) {
          str += `<b>Spellcasting.</b> ${char_util.getStringWithReplacements2({ clean_me: ci.spellcasting_description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
          const resources_entry = ci ? creature.resources_table.getEntry(ci.challenge_rating) : one_resources_entry;
          const ci_spells = ci.use_spells;
          for (const level of ci.spell_levels.sort((a, b) => { return a - b; })) {
            const spells_of_level = ci_spells.filter(o => o.spell_level === level);
            if (spells_of_level.length > 0) {
              if (level === 0) {
                let level_string = "At will: ";
                for (const spell_of_level of spells_of_level) {
                  const spell_id = spell_of_level.spell_id;
                  const spell = spells.find(o => o._id === spell_id);
                  if (spell) {
                    if (level_string.length > 9) {
                      level_string += ", ";
                    }
                    level_string += spell.name;
                  }
                }
                str += level_string + "\n\r___\n\r";
              } else {
                const cast_types = common_util.unique_array(spells_of_level.map(o => o.cast_type));
                for (const cast_type of cast_types) {
                  let level_string = "";
                  switch (cast_type) {
                    case "Slot": 
                      // Check how many slots of this level they have
                      let level_name = "";
                      switch(level) {
                        case 1: level_name = "1st"; break;
                        case 2: level_name = "2nd"; break;
                        case 3: level_name = "3rd"; break;
                        default: level_name = `${level}th`; break;
                      }
                      const slots_of_level = resources_entry && Object.keys(resources_entry.data).includes(`Slot: ${level}`) ? resources_entry.data[ `Slot: ${level}`] : 0;
                      if (slots_of_level) {
                        level_string += `${level_name} level (${slots_of_level}):`;
                      } else if (!resources_entry) {
                        level_string += `${level_name} level (found in the Resources table):`;
                      }
                    break;
                    case "At Will":
                      level_string = "At will: ";
                    break;
                    case "Ritual": 
                      level_string = "As a Ritual: ";
                    break;
                    default:
                      if (spellcasting_entry) {
                        // Check if they're all the same.  They usually are.
                        const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spells_of_level[0].spell_id)[0].x;
                        if (x_day) {
                          let matches = true;
                          for (const spell_of_level of spells_of_level.filter(o => o.cast_type === "X/Day")) {
                            if (spellcasting_entry.spells.filter(o => o.spell_id === spell_of_level.spell_id)[0].x !== x_day) {
                              matches = false;
                              break;
                            }
                          }
                          if (matches) {
                            level_string = `${x_day}/Day each: `;
                          }
                        } else {
                          console.error("x_day missing");
                        }
                      } else {
                        level_string = `A number of times per day found in the Per Day Spells Table: `;
                      }
                    break;
                  }
                  if (level_string.endsWith(": ")) {
                    for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
                      const spell_id = spell_of_level.spell_id;
                      const spell = spells.find(o => o._id === spell_id);
                      if (spell) {
                        if (!level_string.endsWith(": ")) {
                          level_string += ", ";
                        }
                        level_string += spell.name;
                      }
                    }
                    str += level_string + "\n\r___\n\r";
                  } else if (cast_type === "X/Day") {
                    // Show them each in their own row
                    for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
                      const spell_id = spell_of_level.spell_id;
                      const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spell_id)[0].x;
                      const spell = spells.find(o => o._id === spell_id);
                      if (x_day && spell) {
                        str += `${spell.name} (${x_day}/Day)\n\r___\n\r`;
                      }
                    }
                  }
                }
              }
            }
          }
        }
        for (const fb of fbs) {
          str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
        }
      } else {
        const handleSpellcasting = (spellcasting_entry: SpellcastingEntry) => {
          str += `<b>Spellcasting.</b> ${char_util.getStringWithReplacements2({ clean_me: spellcasting_entry.description, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
          const resources_entry = creature.resources_table.getEntry(creature.min_challenge_rating);
          const ci_spells = spellcasting_entry.spells;
          for (const level of spellcasting_entry.spell_levels.sort((a, b) => { return a - b; })) {
            const spells_of_level = ci_spells.filter(o => o.spell_level === level);
            if (spells_of_level.length > 0) {
              if (level === 0) {
                let level_string = "At will: ";
                for (const spell_of_level of spells_of_level) {
                  const spell_id = spell_of_level.spell_id;
                  const spell = spells.find(o => o._id === spell_id);
                  if (spell) {
                    if (level_string.length > 9) {
                      level_string += ", ";
                    }
                    level_string += spell.name;
                  }
                }
                str += level_string + "\n\r___\n\r";
              } else {
                const cast_types = common_util.unique_array(spells_of_level.map(o => o.cast_type));
                for (const cast_type of cast_types) {
                  let level_string = "";
                  switch (cast_type) {
                    case "Slot": 
                      // Check how many slots of this level they have
                      let level_name = "";
                      switch(level) {
                        case 1: level_name = "1st"; break;
                        case 2: level_name = "2nd"; break;
                        case 3: level_name = "3rd"; break;
                        default: level_name = `${level}th`; break;
                      }
                      const slots_of_level = resources_entry && Object.keys(resources_entry.data).includes(`Slot: ${level}`) ? resources_entry.data[ `Slot: ${level}`] : 0;
                      if (slots_of_level) {
                        level_string += `${level_name} level (${slots_of_level}):`;
                      } else if (!resources_entry) {
                        level_string += `${level_name} level (found in the Resources table):`;
                      }
                    break;
                    case "At Will":
                      level_string = "At will: ";
                    break;
                    case "Ritual": 
                      level_string = "As a Ritual: ";
                    break;
                    default:
                      if (spellcasting_entry) {
                        // Check if they're all the same.  They usually are.
                        const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spells_of_level[0].spell_id)[0].x;
                        if (x_day) {
                          let matches = true;
                          for (const spell_of_level of spells_of_level.filter(o => o.cast_type === "X/Day")) {
                            if (spellcasting_entry.spells.filter(o => o.spell_id === spell_of_level.spell_id)[0].x !== x_day) {
                              matches = false;
                              break;
                            }
                          }
                          if (matches) {
                            level_string = `${x_day}/Day each: `;
                          }
                        } else {
                          console.error("x_day missing");
                        }
                      } else {
                        level_string = `A number of times per day found in the Per Day Spells Table: `;
                      }
                    break;
                  }
                  console.log(level_string);
                  if (level_string.endsWith(": ")) {
                    for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
                      const spell_id = spell_of_level.spell_id;
                      const spell = spells.find(o => o._id === spell_id);
                      if (spell) {
                        if (!level_string.endsWith(": ")) {
                          level_string += ", ";
                        }
                        level_string += spell.name;
                      }
                    }
                    str += level_string + "\n\r___\n\r";
                  } else if (cast_type === "X/Day") {
                    // Show them each in their own row
                    for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
                      const spell_id = spell_of_level.spell_id;
                      const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spell_id)[0].x;
                      const spell = spells.find(o => o._id === spell_id);
                      if (x_day && spell) {
                        str += `${spell.name} (${x_day}/Day)\n\r___\n\r`;
                      }
                    }
                  }
                }
              }
            }
          }
        }
        const crs = (common_util.unique_array([...fbs.map(o => o.level),...creature.spellcasting_table.entries.map(o => o.level)])).sort((a, b) => { return a - b; });
        if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
          for (const fb of fbs) {
            str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
          }
          const entry = creature.spellcasting_table.getEntry(creature.min_challenge_rating);
          if (entry) {
            handleSpellcasting(entry);
          }
        } else if (crs.length > 0) {
          for (const cr of crs) {
            str += `### Beginning At CR ${cr}\n\r___\n\r`;
            for (const fb of fbs.filter(o => o.level === cr)) {
              str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
            }
            const entry = creature.spellcasting_table.getEntry(cr);
            if (entry) {
              handleSpellcasting(entry);
            }
          }
        }
      }
    }
    fbs = ci ? creature.legendary_action_fbs.filter(o => o.level <= ci.challenge_rating) : creature.legendary_action_fbs;
    if (fbs.length > 0) {
      str += "## Legendary Actions\n\r___\n\r";
      if (ci) {
        const legendary_actions = creature.legendary_actions_for_cr(ci.challenge_rating);
        str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
      } else if (creature.legendaries_table.entries.length === 1 && 
        creature.legendaries_table.entries[0].level <= creature.min_challenge_rating) {
        const legendary_actions = creature.legendaries_table.entries[0].actions;
        str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
      } else if (creature.legendaries_table.entries.length === 1) {
        str += `### Beginning At CR ${creature.legendaries_table.entries[0].level}\n\r___\n\r`;
        const legendary_actions = creature.legendaries_table.entries[0].actions;
        str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
      } else {
        str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} may be able to take legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn.  See the Legendaries Table to see how many uses if any it has of this feature. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
      }
      if (ci) {
        for (const fb of fbs) {
          str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
        }
      } else {
        const crs = (common_util.unique_array(fbs.map(o => o.level))).sort((a, b) => { return a - b; });
        if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
          for (const fb of fbs) {
            str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
          }
        } else if (crs.length > 0) {
          for (const cr of crs) {
            if (crs.length > 1) {
              str += `### Beginning At CR ${cr}\n\r___\n\r`;
            }
            for (const fb of fbs.filter(o => o.level === cr)) {
              str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
            }
          }
        }
      }
    }
    return str;
  }

  // const reparse = () => {
  //   // This goes through all of the features descriptions and reparses them.
  //   // I may add other things to it later on.
  //   const new_obj = obj;

  //   const reparseCreatureAbility = (ca: CreatureAbility) => {
  //     let using_ability: AbilityScoreType | "" = "";
  //     let using_ability_mod = -5;
  //     let name = ca.name;
  //     while (ca.description.includes(" ")) {
  //       ca.description = ca.description.replace(" ", " ");
  //     }
  //     let content_str = ca.description;
  //     ca.effects = [];
  //     // debugger;
  //     if (content_str.includes("Attack")) { // Melee Weapon Attack:
  //       let sub = content_str.substring(content_str.indexOf("+")); // +12 to hit
  //       sub = sub.substring(0, sub.indexOf(" ")).replace("+","").trim();
  //       const sub_num = +sub;
  //       if (!isNaN(sub_num)) {
  //         const ability_mod = sub_num - new_obj.proficiency_modifier;
  //         if (content_str.includes("Spell")) {
  //           // It should be the new_obj.spellcasting_ability
  //           if (new_obj.spellcasting_ability !== "None") {
  //             const mod = new_obj.ability_scores.getBasicModifier(new_obj.spellcasting_ability);
  //             if (mod === ability_mod) {
  //               using_ability = new_obj.spellcasting_ability;
  //               using_ability_mod = mod;
  //             } else {
  //               console.error("It should never come here.");
  //             }
  //           } else {
  //             // This means that they have Actions that use Spell Attacks, but no actual spells.
  //             // In that situation they probably use the same ability for all such actions, 
  //             // so once it's identified, we will set it as their spellcasting ability.
  //             // Monsters usually use CHA, so check that first. Next INT, then WIS
  //             let mod = new_obj.ability_scores.getBasicModifier("CHA");
  //             if (mod === ability_mod) {
  //               using_ability = "CHA";
  //             } else {
  //               mod = new_obj.ability_scores.getBasicModifier("INT");
  //               if (mod === ability_mod) {
  //                 using_ability = "INT";
  //               } else {
  //                 mod = new_obj.ability_scores.getBasicModifier("WIS");
  //                 if (mod === ability_mod) {
  //                   using_ability = "WIS";
  //                 }
  //               }
  //             }
  //             if (using_ability !== "") {
  //               using_ability_mod = mod;
  //             }
  //           }
  //         } else if (content_str.includes("Melee")) {
  //           // Check STR first
  //           let mod = new_obj.ability_scores.getBasicModifier("STR");
  //           if (mod === ability_mod) {
  //             using_ability = "STR";
  //           } else {
  //             mod = new_obj.ability_scores.getBasicModifier("DEX");
  //             if (mod === ability_mod) {
  //               using_ability = "DEX";
  //             } else {
  //               console.error("It should never come here.");
  //             }
  //           }
  //           if (using_ability !== "") {
  //             using_ability_mod = mod;
  //           }
  //         } else {
  //           // Check DEX first
  //           let mod = new_obj.ability_scores.getBasicModifier("DEX");
  //           if (mod === ability_mod) {
  //             using_ability = "DEX";
  //           } else {
  //             mod = new_obj.ability_scores.getBasicModifier("STR");
  //             if (mod === ability_mod) {
  //               using_ability = "STR";
  //             } else {
  //               console.error("It should never come here.");
  //             }
  //           }
  //           if (using_ability !== "") {
  //             using_ability_mod = mod;
  //           }
  //         }
  //         if (using_ability !== "") {
  //           ca.attack_bonus.base = 0;
  //           if (using_ability === new_obj.spellcasting_ability) {
  //             ca.attack_bonus.add_spell_attack_mult = 1;
  //             ca.description = ca.description.replace(`Attack: +${sub}`, "Attack: +{cfs.spell_attack}");
  //           } else {
  //             ca.attack_bonus.add_prof_bonus_mult = 1;
  //             ca.attack_bonus.add_ability_mod_mult = 1;
  //             ca.attack_bonus.add_ability_scores = [using_ability];
  //             ca.description = ca.description.replace(`Attack: +${sub}`, `Attack: {attack_string.${using_ability}}`);
  //             // ca.description = ca.description.replace(`Attack: +${sub}`, `Attack: {attack_string.${using_ability}}`);
  //           }
  //         }
  //         // ca.attack_bonus.base = +sub;
  //       }
  //     }
  //     if (content_str.includes("DC")) { // DC 19 Dexterity saving throw
  //       let sub = content_str.substring(content_str.indexOf("DC") + 2);
  //       sub = sub.substring(0, sub.indexOf("sav") - 1).trim(); // 19 Dexterity
  //       const pieces = sub.split(" ");
  //       const dc = +pieces[0];
  //       const ability_mod = dc - 8 - new_obj.proficiency_modifier;
  //       // if (new_obj.spellcasting_ability !== "None") {
  //       //   if (ability_mod === new_obj.ability_scores.getBasicModifier(new_obj.spellcasting_ability)) {
  //       //     ca.dc.add_spell_save_dc_mult = 1;
  //       //     using_ability = new_obj.spellcasting_ability;
  //       //     using_ability_mod = ability_mod;
  //       //     ca.description = ca.description.replace(`DC ${dc}`, "DC {cfs.spell_dc}");
  //       //   }
  //       // }
  //       if (ca.name.includes("Breath")) {
  //         // Breath Weapons the DC is generally based on CON.
  //         if (modFromScore(new_obj.ability_scores.CON.base) === ability_mod) {
  //           using_ability = "CON";
  //         }
  //       }
  //       if (using_ability === "") {
  //         // This order is the most likely.
  //         if (modFromScore(new_obj.ability_scores.CHA.base) === ability_mod) {
  //           using_ability = "CHA";
  //         } else if (modFromScore(new_obj.ability_scores.STR.base) === ability_mod) {
  //           using_ability = "STR";
  //         } else if (modFromScore(new_obj.ability_scores.DEX.base) === ability_mod) {
  //           using_ability = "DEX";
  //         } else if (modFromScore(new_obj.ability_scores.WIS.base) === ability_mod) {
  //           using_ability = "WIS";
  //         } else if (modFromScore(new_obj.ability_scores.INT.base) === ability_mod) {
  //           using_ability = "INT";
  //         } else if (modFromScore(new_obj.ability_scores.CON.base) === ability_mod) {
  //           using_ability = "CON";
  //         }
  //       }
  //       if (using_ability === "") {
  //         // Could be anything.  Just leave it as a base.
  //         ca.dc.base = dc;
  //       } else {
  //         ca.dc.base = 8;
  //         ca.dc.add_prof_bonus_mult = 1;
  //         ca.dc.add_ability_mod_mult = 1;
  //         ca.dc.add_ability_scores.push(using_ability);
  //         ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
  //         using_ability_mod = ability_mod;
  //       }
  //       ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[pieces[1]];
  //     }
  //     if (content_str.includes("reach")) {
  //       let sub = content_str.substring(content_str.indexOf("reach") + 5).trim();
  //       sub = sub.substring(0, sub.indexOf(" ")).trim();
  //       ca.range = sub;
  //     } else if (content_str.includes("range")) {
  //       let sub = content_str.substring(content_str.indexOf("range") + 5).trim();
  //       sub = sub.substring(0, sub.indexOf(" ")).trim();
  //       ca.range = sub;
  //     }
  //     let damage_str = content_str.toLocaleLowerCase();
  //     const lower_case = ca.description.toLowerCase();
  //     if (name.includes("Wing Attack")) {
  //       console.log(damage_str);
  //     }
  //     while (damage_str.includes("damage")) {
  //       let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
  //       damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
  //       const words = damage_sub.split(" ");
  //       // console.log(words);
  //       const damage_type = common_util.capitalize_first(words[words.length - 1]);
  //       if (damage_sub.includes("(") && damage_sub.includes(")")) {
  //         let start_index = damage_sub.indexOf("(");
  //         start_index -= 2;
  //         while (damage_sub[start_index] !== " ") {
  //           start_index--;
  //         }
  //         let replace_me = damage_sub.substring(start_index);
  //         const end_index = replace_me.indexOf(")") + 1;
  //         replace_me = replace_me.substring(0, end_index).trim();
  //         damage_sub = damage_sub.substring(damage_sub.indexOf("("));
  //         damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
  //         damage_sub = damage_sub.replace("(","").replace(")","");
  //         let count = 0;
  //         let size = 0;
  //         let flat = 0;
  //         if (damage_sub.includes("+")) {
  //           const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
  //           damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
  //           flat = +flat_str;
  //         } else if (damage_sub.includes("-")) {
  //           const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
  //           damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
  //           flat = -1 * +flat_str;
  //         } else if (damage_sub.includes("−")) {
  //           const flat_str = damage_sub.substring(damage_sub.indexOf("−")).replace("−","");
  //           damage_sub = damage_sub.substring(0, damage_sub.indexOf("−")).replace("−","");
  //           flat = -1 * +flat_str;
  //         }
  //         if (damage_sub.includes("d")) {
  //           const pieces = damage_sub.split("d");
  //           count = +pieces[0];
  //           size = +pieces[1];
  //         }
  //         const effect = new AbilityEffect();
  //         effect.type = "Damage";
  //         effect.damage_types.push(DAMAGE_TYPES.includes(damage_type) ? damage_type as DamageType : "Typeless");
  //         if (ca.effects.length === 0) {
  //           const first_part = lower_case.substring(0, 20);
  //           if (first_part.includes("melee")) {
  //             if (first_part.includes("spell")) {
  //               effect.attack_type = "Melee Spell";
  //             } else {
  //               effect.attack_type = "Melee";
  //             }
  //           } else if (first_part.includes("ranged")) {
  //             if (first_part.includes("spell")) {
  //               effect.attack_type = "Ranged Spell";
  //             } else {
  //               effect.attack_type = "Ranged";
  //             }
  //           } else if (ca.saving_throw_ability_score !== "") {
  //             effect.attack_type = "Save";
  //             effect.saving_throw_ability_score = ca.saving_throw_ability_score;
  //           }
  //           if (ca.range !== "") {
  //             try {
  //               const range = +ca.range;
  //               if (!isNaN(range)) {
  //                 effect.target_int.range.base = range;
  //               }
  //             } catch (ex) { }
  //           }
  //           // X-foot cone
  //           if (ca.description.includes("-foot")) {
  //             const foot_index = ca.description.indexOf("-foot");
  //             let area_str = "";
  //             let i = 1;
  //             while (ca.description[foot_index - i] !== " ") {
  //               area_str = ca.description[foot_index - i] + area_str;
  //               ++i;
  //             }
  //             // debugger;
  //             i = 6;
  //             let shape_str = "";
  //             while (ca.description[foot_index + i] !== " ") {
  //               shape_str += ca.description[foot_index + i];
  //               ++i;
  //             }
  //             if (shape_str.endsWith(".")) {
  //               shape_str = shape_str.substring(0, shape_str.length - 1);
  //             }
  //             effect.target_int.target_type = "Area";
  //             try {
  //               const area = +area_str;
  //               if (!isNaN(area)) {
  //                 effect.target_int.area.base = area;
  //               }
  //             } catch (ex) { }
  //             shape_str = common_util.capitalize_first(shape_str);
  //             if (AREA_TYPES.includes(shape_str)) {
  //               effect.target_int.area_type = shape_str as AreaType;
  //             } else {
  //               effect.target_int.area_type = "Sphere";
  //             }
  //           }
  //         }
  //         const potence = new Potence();
  //         potence.level = 1;
  //         potence.rolls.count.base = count;
  //         potence.rolls.size.base = size;
  //         // console.log(damage_str);
  //         // console.log(damage_sub);
  //         if (flat !== 0) {
  //           if (using_ability !== "" && using_ability_mod <= flat) {
  //             potence.rolls.flat.base = flat - using_ability_mod;
  //             potence.rolls.flat.add_ability_mod_mult = 1;
  //             if (using_ability === new_obj.spellcasting_ability) {
  //               potence.rolls.flat.add_ability_scores.push("Spellcasting");
  //             } else {
  //               potence.rolls.flat.add_ability_scores.push(using_ability);
  //             }
  //           } else {
  //             potence.rolls.flat.base = flat;
  //           }
  //         }
  //         ca.description = ca.description.replace(replace_me, potence.potence_string);
  //         effect.potences.push(potence);
  //         ca.effects.push(effect);
  //       }
  //     }
  //     if (ca.effects.length === 0) {
  //       const effect = new AbilityEffect();
  //       if (ca.dc.isBlank()) {
  //         effect.type = "Utility";
  //       } else {
  //         effect.type = "Control";
  //         effect.attack_type = "Save";
  //         effect.saving_throw_ability_score = ca.saving_throw_ability_score;
  //         for (const condition of conditions.filter(o => o.common)) {
  //           const condition_name = condition.name;
  //           if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
  //             effect.utility_detail = condition_name;
  //           }
  //         }
  //       }
  //       ca.effects.push(effect);
  //     }
  //     const ff = api.all_floating_features.find(o => o._id === ca._id);
  //     if (ff) {
  //       ff.copy(ca);
  //     }
  //   }

  //   for (const fb of new_obj.action_fbs) {
  //     const ff = fb.features[0].floating_feature;
  //     if (ff instanceof CreatureAbility) {
  //       reparseCreatureAbility(ff);
  //     }
  //   }
  //   for (const fb of new_obj.legendary_action_fbs) {
  //     const ff = fb.features[0].floating_feature;
  //     if (ff instanceof CreatureAbility) {
  //       reparseCreatureAbility(ff);
  //     }
  //   }
  //   for (const fb of new_obj.lair_action_fbs) {
  //     const ff = fb.features[0].floating_feature;
  //     if (ff instanceof CreatureAbility) {
  //       reparseCreatureAbility(ff);
  //     }
  //   }
  //   for (const fb of new_obj.traits) {
  //     const ff = fb.features[0].floating_feature;
  //     if (ff instanceof CreatureAbility) {
  //       reparseCreatureAbility(ff);
  //     }
  //   }
  //   setObj(new_obj);
  //   updateCreatureInstances(new_obj);
  //   setChangeTime(common_util.get_time_number());
  // }

  const parse = () => {
    const elem = parseRef.current as HTMLElement;
    // debugger;
    let stat_block_elements = elem.getElementsByClassName("mon-stat-block-2024");
    if (stat_block_elements.length === 0) {
      stat_block_elements = elem.getElementsByClassName("mon-stat-block");
    }
    if (stat_block_elements.length === 0) {
      stat_block_elements = elem.getElementsByClassName("stat-block");
    }
    if (stat_block_elements.length === 1) {
      // while (elem && !elem.classList.contains("stat-block") && !elem.classList.contains("mon-stat-block") && !elem.classList.contains("mon-stat-block-2024")) {
      //   elem = elem.firstElementChild as (HTMLElement | null);
      // }
      debugger;
      api.parseCreature(obj, stat_block_elements[0] as HTMLElement).then(new_obj => {
        setObj(new_obj);
        updateCreatureInstances(new_obj);
        setChangeTime(common_util.get_time_number());
      });
    }
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth - 1} message="Loading Creatures" />);
  } else { 
    const menu_options: IStringHash = disabled ? {
      "Home": "main",
      "HP": "hp",
      "Attributes": "attributes",
      "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table"
    } : {
      "Home": "main",
      "HP": "hp",
      "Attributes": "attributes",
      "More Attributes": "more_attributes",
      "Even More Attributes": "even_more_attributes",
      "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table",
      "Parse": "parse",
    };
    // if (obj.ridable_type !== "None") {
    //   delete menu_options["Lair Actions"];
    //   delete menu_options["Legendary Actions"];
    // }
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          ref={heightFinder}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <ArrowBackButton
                onClick={ () => {
                  setNavigateTo(`/dandd/creature`);
                }}
              />&nbsp;
              <span className="header">
                { obj._id === "" ? "Create Creature" : `Edit ${obj.name}` }
              </span>
            </Grid>
            { !disabled &&
              <Grid
                size={{
                  xs: 12
                }}>
                <TemplateBox
                  obj={obj}
                  type="Creature"
                  useTemplate={(template: TemplateBase) => {
                    const new_obj = obj;
                    new_obj.copyTemplate(template);
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
            <Grid
              size={{
                xs: 12
              }}>
              <FloatMenu
                option_map={menu_options}
                selected={tab}
                onSelect={(changed: string) => {
                  // if (changed.includes("actions") || changed === "traits") {
                  //   updateCreatureInstance();
                  // }
                  setTab(changed as TabType);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          style={{ 
            height: (tab === "main" ? `${window.innerHeight - (topHeight + 106)}px` : `${window.innerHeight - (topHeight + 64)}px`), 
            width: "100%",
            overflowY: "auto", 
            overflowX: "hidden"
          }}
          size={{
            xs: 12
          }}>
          {/* { window.innerHeight } - ({ topHeight } + 64) = {window.innerHeight - (topHeight + 64)} */}
          { render_tab() }
        </Grid>
      </Grid>
    ); 
  }
}
